import { Save, Shield, ShieldUser } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@unistocks/components/button/button';
import { Form } from '@unistocks/components/form/form';
import { requiredSchema } from '@unistocks/components/form/validators';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Page } from '@unistocks/components/page/page';
import { Heading } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import { PermissionManagementSheet } from '@unistocks/features/admin/components/permission-management/permission-management-sheet';
import { IdentityRoleUpdateDto } from '@unistocks/proxy/axios-client';
import {
  roleGetQueryKey,
  useRoleGetQuery,
  useRoleUpdateMutation
} from '@unistocks/proxy/axios-client/RoleQuery';
import { usePermission } from '@unistocks/shared/hooks/use-permission';
import { t } from '@unistocks/shared/localization/index.ts';
import { UnistocksPermissions } from '@unistocks/shared/permissions';
import { showErrorToast } from '@unistocks/shared/utils/toast';
import { useRouter } from 'expo-router';
import React, { useState } from 'react';
import { z } from 'zod';

type RoleDetailsProps = {
  id: string;
};

const schemaFactory = () =>
  z.object({
    name: requiredSchema(z.string(), t('AbpIdentity::RoleName')),
    isDefault: z.boolean().default(false),
    isPublic: z.boolean().default(false),
    concurrencyStamp: z.string().nullish().default(null),
    extraProperties: z.record(z.string(), z.any()).default({})
  });

export const RoleDetails: React.FC<RoleDetailsProps> = ({ id }) => {
  const schema = schemaFactory();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [permissionsSheetOpen, setPermissionsSheetOpen] = useState(false);

  const hasEditRole = usePermission(UnistocksPermissions.RolesUpdate);
  const hasManagePermissions = usePermission(UnistocksPermissions.RolesManagePermissions);

  const { data: role, isSuccess } = useRoleGetQuery(id);

  const { mutateAsync: updateRole } = useRoleUpdateMutation(id, {
    onSuccess: () => {
      toast.success(t('AbpUi::SavedSuccessfully'));
      queryClient.invalidateQueries({ queryKey: roleGetQueryKey(id) });
      router.back();
    },
    onError: e => {
      showErrorToast(e);
    }
  });

  const initialValues = isSuccess ? (role as IdentityRoleUpdateDto) : null;

  const handleSubmit = (values: IdentityRoleUpdateDto) => updateRole(values);

  return (
    <Page size="medium">
      <Page.ScrollView
        headerTitle={`${t('::Edit')} ${role?.name}`}
        breadcrumbs={[
          {
            key: 'roles',
            href: '/(admin)/role-management',
            name: t('AbpIdentity::Roles'),
            rootPathName: 'roles',
            Icon: props => <ShieldUser {...props} />
          }
        ]}
        queryState={{
          queryKeys: [roleGetQueryKey(id)]
        }}
      >
        {isSuccess ? (
          <Column gap={'$4'} mt={'$3'}>
            <Page.Content>
              <Form
                schema={schema}
                initialValues={initialValues!}
                triggerText={t('AbpUi::Save')}
                triggerIcon={Save}
                onSubmit={handleSubmit}
                disabled={!hasEditRole}
                customTrigger={hasEditRole ? undefined : () => null}
              >
                <Form.Input
                  name="name"
                  isRequired
                  isDisabled={role.isStatic}
                  label={t('AbpIdentity::RoleName')}
                  placeholder={t('AbpIdentity::RoleName')}
                />
                <Form.Checkbox name="isDefault" label={t('AbpIdentity::DisplayName:IsDefault')} />
                <Form.Checkbox name="isPublic" label={t('AbpIdentity::DisplayName:IsPublic')} />
                <Form.Checkbox
                  name="isStatic"
                  label={t('AbpIdentity::DisplayName:IsStatic')}
                  isDisabled
                />
              </Form>
            </Page.Content>

            {hasManagePermissions && (
              <Page.Content>
                <Row justifyContent="space-between" alignItems="center">
                  <Heading size={'$6'}>{t('AbpIdentity::Permissions')}</Heading>
                  <Button
                    variant={'outlined'}
                    size={'$3'}
                    Icon={Shield}
                    onPress={() => setPermissionsSheetOpen(true)}
                  >
                    {t('AbpIdentity::ManagePermissions')}
                  </Button>
                </Row>
              </Page.Content>
            )}
          </Column>
        ) : null}
      </Page.ScrollView>

      <PermissionManagementSheet
        providerName="R"
        providerKey={role?.name ?? ''}
        open={permissionsSheetOpen}
        onOpenChange={setPermissionsSheetOpen}
      />
    </Page>
  );
};

RoleDetails.displayName = 'RoleDetails';
